import {
  Button,
  type ButtonProps,
  type PolymorphicComponentProps,
} from "@mantine/core";
import Link from "next/link";
import { type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import type { Maybe } from "@/types/utils";

interface DataAndAriaAttributes {
  [key: `data-${string}`]: Maybe<string>;
  [key: `aria-${string}`]: Maybe<string>;
}

type AcceptedComponentType = typeof Link | "button";
// Mantine's ButtonProps are only the "own" props of the button component
type ActualButtonProps<C extends AcceptedComponentType> =
  PolymorphicComponentProps<C, ButtonProps> & DataAndAriaAttributes;

interface ToggleButtonProps<C extends AcceptedComponentType> {
  compact?: boolean;
  leftActive: boolean;
  leftButtonProps: ActualButtonProps<C>;
  leftContent: ReactNode;
  rightButtonProps: ActualButtonProps<C>;
  rightContent?: ReactNode;
}

const SHARED_STYLES = [
  "border",
  "hover:text-gray-900",
  "text-sm",
  "transition-colors",
];
const ACTIVE_STYLES = [
  "bg-uw-purple-50",
  "border-uw-purple",
  "font-medium",
  "hover:bg-uw-purple-50",
  "text-gray-900",
];
const INACTIVE_STYLES = [
  "bg-white",
  "border-gray-300",
  "font-normal",
  "hover:bg-purple-50",
  "text-gray-600",
];

export const ToggleButton = <C extends AcceptedComponentType = "button">({
  compact = false,
  leftActive,
  leftButtonProps,
  leftContent,
  rightButtonProps,
  rightContent,
}: ToggleButtonProps<C>) => {
  // To enable the consumer to overwrite any base button props but have styles
  // merged rather than overwritten, we pass the merged className to the child
  // button components after spreading. Destructuring className from the button
  // props in the params would be more clear, but that causes TypeScript to get
  // very upset for reasons that are unclear to me.
  return (
    <Button.Group>
      <Button<C>
        // data-active for ease of styling by consumers
        data-active={leftActive}
        size={compact ? "xs" : "sm"}
        variant="light"
        {...leftButtonProps}
        className={twMerge([
          SHARED_STYLES,
          leftActive ? ACTIVE_STYLES : INACTIVE_STYLES,
          leftButtonProps.className,
        ])}
      >
        {leftContent}
      </Button>
      {rightContent && (
        <Button<C>
          data-active={!leftActive}
          size={compact ? "xs" : "sm"}
          variant="light"
          {...rightButtonProps}
          className={twMerge([
            SHARED_STYLES,
            leftActive ? INACTIVE_STYLES : ACTIVE_STYLES,
            rightButtonProps.className,
          ])}
        >
          {rightContent}
        </Button>
      )}
    </Button.Group>
  );
};
